/**
 * Competitive Landscape Service
 *
 * API client for the actor model, category board, and classification system.
 * Endpoints: /api/competitive-landscape/*
 */

import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

export interface Actor {
  id: string;
  domain: string;
  name: string | null;
  site_type: string;
  classification_source: string | null;
  classification_confidence: number | null;
  logo_url: string | null;
  metadata: Record<string, any>;
  first_seen_at: string;
  last_seen_at: string;
  role: string | null;
  role_source: string | null;
  relevance_score: number | null;
  notes: string | null;
}

export interface ActorDetail extends Actor {
  contentScores: {
    avg_score: number | null;
    avg_content_depth: number | null;
    avg_schema_coverage: number | null;
    avg_faq_coverage: number | null;
    pages_analyzed: number;
  } | null;
  sovMetrics: {
    mention_count: number;
    across_queries: number;
    by_providers: string[];
  } | null;
}

export interface CategoryBoardEntry {
  category: string;
  actors: CategoryActor[];
  ourPosition: number | null;
  demand: {
    commercialValue: number;
    clusterCount: number;
    coveragePct: number;
  } | null;
}

export interface CategoryActor {
  domain: string;
  name: string | null;
  siteType: string;
  role: string | null;
  compositeScore: number;
  sovPct: number;
  avgSerpPosition: number | null;
  rank: number;
}

export interface ActorCategoryMetric {
  category_path: string;
  composite_authority_score: number;
  sov_pct: number;
  sov_mention_count: number;
  sov_total_checks: number;
  avg_serp_position: number | null;
  serp_appearances: number;
  best_serp_position: number | null;
  avg_content_depth_score: number | null;
  avg_schema_coverage_score: number | null;
  avg_faq_coverage_score: number | null;
  pages_captured: number;
  sov_providers: string[];
  period_start: string;
  period_end: string;
}

export interface LandscapeStats {
  totalActors: number;
  unclassified: number;
  byRole: { role: string; count: number }[];
  byType: { site_type: string; count: number }[];
}

export interface CategoryActorEntry {
  actor_id: string;
  domain: string;
  name: string | null;
  site_type: string;
  role: string | null;
  composite_authority_score: number;
  sov_pct: number;
  sov_mention_count: number;
  avg_serp_position: number | null;
  serp_appearances: number;
  best_serp_position: number | null;
  avg_content_depth_score: number | null;
  avg_schema_coverage_score: number | null;
  avg_faq_coverage_score: number | null;
  pages_captured: number;
  sov_providers: string[];
}

// Phase 4: Actions
export interface ActionEntry {
  id: string;
  type: string;
  timestamp: string;
  url: string | null;
  pageTitle: string | null;
  source: string;
  categoryPath: string | null;
  impactStatus: 'positive' | 'neutral' | 'negative' | 'pending' | 'too_early';
  positionBefore: number | null;
  positionAfter: number | null;
  positionDelta: number | null;
  sovBeforePct: number | null;
  sovAfterPct: number | null;
  sovDeltaPct: number | null;
  impactValueEur: number | null;
  timeToImpactDays: number | null;
}

export interface ActionsSummary {
  totalActions: number;
  positive: number;
  neutral: number;
  negative: number;
  pending: number;
  tooEarly: number;
  avgTimeToImpactDays: number | null;
}

// Phase 5: Aggregators & Influencers
export interface AggregatorEntry {
  id: string;
  domain: string;
  name: string | null;
  siteType: string;
  role: string;
  relevanceScore: number;
  categoriesPresent: number;
  categoriesMissing: number;
  avgAuthority: number | null;
  avgSovPct: number | null;
  totalMentions: number;
}

export interface InfluencerEntry {
  id: string;
  domain: string;
  name: string | null;
  siteType: string;
  role: string;
  relevanceScore: number;
  categoriesActive: number;
  avgAuthority: number | null;
  avgSovPct: number | null;
  totalMentions: number;
  providers: string[];
}

// ============================================================================
// Service
// ============================================================================

class CompetitiveLandscapeService {

  async getActors(siteId: string, filters?: { role?: string; siteType?: string }): Promise<{ actors: Actor[]; count: number }> {
    const params = new URLSearchParams({ siteId });
    if (filters?.role) params.set('role', filters.role);
    if (filters?.siteType) params.set('siteType', filters.siteType);
    const response = await authFetch(`/api/competitive-landscape/actors?${params}`);
    if (!response.ok) throw new Error(`Failed to fetch actors (${response.status})`);
    return response.json();
  }

  async getActorDetail(actorId: string, siteId: string): Promise<ActorDetail> {
    const response = await authFetch(`/api/competitive-landscape/actors/${actorId}?siteId=${siteId}`);
    if (!response.ok) throw new Error(`Failed to fetch actor detail (${response.status})`);
    const data = await response.json();
    return { ...data.actor, contentScores: data.contentScores, sovMetrics: data.sovMetrics };
  }

  async getCategoryBoard(siteId: string, days = 7, market?: string): Promise<{ board: CategoryBoardEntry[]; categoryCount: number }> {
    const params = new URLSearchParams({ siteId, days: String(days) });
    if (market) params.set('market', market);
    const response = await authFetch(`/api/competitive-landscape/category-board?${params}`);
    if (!response.ok) throw new Error(`Failed to fetch category board (${response.status})`);
    return response.json();
  }

  async getActorCategories(actorId: string, siteId: string, days = 7): Promise<{ actorId: string; categories: ActorCategoryMetric[] }> {
    const response = await authFetch(`/api/competitive-landscape/actor-categories/${actorId}?siteId=${siteId}&days=${days}`);
    if (!response.ok) throw new Error(`Failed to fetch actor categories (${response.status})`);
    return response.json();
  }

  async getCategoryActors(categoryPath: string, siteId: string, days = 7): Promise<{ category: string; actors: CategoryActorEntry[] }> {
    const encoded = encodeURIComponent(categoryPath);
    const response = await authFetch(`/api/competitive-landscape/category/${encoded}?siteId=${siteId}&days=${days}`);
    if (!response.ok) throw new Error(`Failed to fetch category actors (${response.status})`);
    return response.json();
  }

  async getStats(siteId: string): Promise<LandscapeStats> {
    const response = await authFetch(`/api/competitive-landscape/stats?siteId=${siteId}`);
    if (!response.ok) throw new Error(`Failed to fetch stats (${response.status})`);
    return response.json();
  }

  async updateActorRole(actorId: string, siteId: string, role: string, notes?: string): Promise<void> {
    const response = await authFetch(`/api/competitive-landscape/actors/${actorId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteId, role, notes })
    });
    if (!response.ok) throw new Error(`Failed to update role (${response.status})`);
  }

  async updateActorClassification(actorId: string, siteType: string): Promise<void> {
    const response = await authFetch(`/api/competitive-landscape/actors/${actorId}/classification`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ siteType })
    });
    if (!response.ok) throw new Error(`Failed to update classification (${response.status})`);
  }

  async triggerClassification(siteId: string): Promise<{ actors: number; classified: number; roles: number }> {
    const response = await authFetch(`/api/competitive-landscape/classify?siteId=${siteId}`, { method: 'POST' });
    if (!response.ok) throw new Error(`Failed to trigger classification (${response.status})`);
    return response.json();
  }

  // Phase 4: Actions
  async getActions(siteId: string, days = 90, status?: string): Promise<{ actions: ActionEntry[]; count: number }> {
    const params = new URLSearchParams({ siteId, days: String(days) });
    if (status) params.set('status', status);
    const response = await authFetch(`/api/competitive-landscape/actions?${params}`);
    if (!response.ok) throw new Error(`Failed to fetch actions (${response.status})`);
    return response.json();
  }

  async getActionsSummary(siteId: string, days = 30): Promise<ActionsSummary> {
    const response = await authFetch(`/api/competitive-landscape/actions/summary?siteId=${siteId}&days=${days}`);
    if (!response.ok) throw new Error(`Failed to fetch actions summary (${response.status})`);
    return response.json();
  }

  // Phase 5: Aggregators & Influencers
  async getAggregators(siteId: string): Promise<{ aggregators: AggregatorEntry[]; totalCategories: number }> {
    const response = await authFetch(`/api/competitive-landscape/aggregators?siteId=${siteId}`);
    if (!response.ok) throw new Error(`Failed to fetch aggregators (${response.status})`);
    return response.json();
  }

  async getInfluencers(siteId: string): Promise<{ influencers: InfluencerEntry[] }> {
    const response = await authFetch(`/api/competitive-landscape/influencers?siteId=${siteId}`);
    if (!response.ok) throw new Error(`Failed to fetch influencers (${response.status})`);
    return response.json();
  }
}

export const competitiveLandscapeService = new CompetitiveLandscapeService();
